/**
 * @prettier
 */

/* This file is a copy for existing AcuantCamera screen with update related to processing state, it is not refactored since going to be deprecated soon*/
// React Packages
import React, { Component } from "react";
import { connect } from "react-redux";
import { Trans, withTranslation } from "react-i18next";
import { bindActionCreators } from "redux";
import { history } from "../../state/store";
import { titleMap } from "../../config/documents";

// Actions
import {
    setRetryLimit,
    submissionAttempt,
} from "./../../state/actions/submissionStateActions";
import {
    clearCapturedDocumentImages,
    setCapturedDocumentImage,
} from "../../state/actions/idPropertiesActions";
import { setAutoIDCaptureError } from "../../state/actions/configActions";

// Components
import Header from "../../components/Header";
import Navigator from "../../components/Navigator";
import CustomButton from "../../components/CustomButton";
import ProcessingLayout, {
    LoadingStateAnimation,
} from "../../components/ProcessingLayout";
import StageComplete from "../../components/Messages/StageComplete";
import OutOfRecaptures from "../../components/Messages/OutOfRecaptures";
import { ModalPopupOverlay } from "../../components/ModalPopupOverlay";

// Services
import logService from "../../services/shared/logService";
import DataDogService from "../../services/shared/datadogService";
import { makeCommaSeparatedString } from "../../services/shared/formatting";

// Config
import { ACTION_LABELS } from "../../config/dataDogActionLabels";
import { imageAlt } from "../../config/accessabilityRules";
import { environment } from "../../config/environment";

// Constants
import FailedToAcceptDocumentIcon from "../../assets/svg/DynamicColoredIcons/FailedToAcceptDocumentIcon";
import { useRotatableImageContainer } from "../ProcessedImageResultPage/components/useRotatableImageContainer";
import SubmissionFlowStepper from "../../components/SubmissionFlowStepper";
const ACTION = ACTION_LABELS.acuantCamera;

// Global
let loadingTimer = null;
let isMounted = true;

const AcuantComponentWrapper = props => {
    const { ImageContainer, handleRotation } = useRotatableImageContainer();
    return (
        <AcuantCameraPage
            {...props}
            ImageContainer={ImageContainer}
            handleRotation={handleRotation}
        />
    );
};
class AcuantCameraPage extends Component {
    constructor(props) {
        super(props);
        this.primaryFocusRef = React.createRef();
        this.detectedCount = 0;
        this.brandingColor = `#${props.branding.data.company_branding.primary_color}`;
        this.state = {
            processing: false,
            cropMessage: false,
            receivedImage: false,
            previewImage: null,
            autoCaptureError: false,
            autoCaptureStarted: false,
            navigation: {
                action: "load",
                props: null,
            },
        };
    }

    componentDidMount() {
        // set mounted on first render
        isMounted = true;

        if (
            this.props.submissionAttempts.remaining > 0 ||
            this.props.sidesLeft > 0 ||
            this.props.submissionAttempts[
                this.props.sidesLeft === 2 ? "front" : "back"
            ] === 0
        ) {
            this.clearLoading();
            this.startCamera();
        }

        // Sets focus to primary heading on first render
        if (this.primaryFocusRef && this.primaryFocusRef.current) {
            this.primaryFocusRef.current.focus();
        }

        // Set document title
        this.setDocumentTitle();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.setDocumentTitle();
    }

    componentWillUnmount() {
        // set mounted false on unmount
        isMounted = false;
        this.clearCameraStreams();
    }

    clearCameraStreams() {
        AcuantCamera.end();
        AcuantCameraUI.end();

        const videoElement = document.getElementById("acuant-player");
        if (videoElement) {
            videoElement?.pause();

            const { srcObject } = videoElement;
            if (srcObject) {
                srcObject?.getTracks().forEach(track => {
                    if (track) {
                        track?.stop();
                    }
                });
            }
            videoElement.srcObject = null;
        }
    }

    setDocumentTitle() {
        const { t } = this.props;
        if (this.state.autoCaptureError) {
            document.title = t("idpal_doc_title_capture_id_error");
        } else if (this.props.completed) {
            document.title = t("idpal_doc_title_message_complete");
        } else if (this.checkIsOutOfRetries()) {
            document.title = t("idpal_doc_title_message_attempts");
        } else {
            document.title = t("idpal_doc_title_capture_id");
        }
    }

    // Out of retries, and we've already tried this side
    checkIsOutOfRetries() {
        return (
            this.props.submissionAttempts.remaining <= 0 &&
            this.props.sidesLeft === 0 &&
            this.props.submissionAttempts[
                this.props.sidesLeft === 2 ? "front" : "back"
            ] > 0
        );
    }

    // This function manipulates the DOM directly to cover gap between file selected and callback from SDK.
    // Otherwise, the "analyzing" gif will not show until onManualCapture is complete, which can take up to 3 Seconds.
    // If we have no file by 1 seconds of this being triggered, hide the dummy loader and prompt the user to retry.
    smoothLoading() {
        if (!loadingTimer) {
            const loader = document.getElementById("loader");
            const retry = document.getElementById("retry");
            if (loader && loader.classList) {
                loader.classList.remove("hidden");
            }
            if (retry && retry.classList) {
                retry.classList.add("hidden");
            }

            loadingTimer = setTimeout(() => {
                document.body.onfocus = null;
                const backButton = document.getElementById("back");
                if (backButton) {
                    backButton.click();
                }
            }, 1000);
        }
    }

    clearLoading() {
        document.body.onfocus = null;
        const loader = document.getElementById("loader");
        if (loader && loader.classList) {
            loader.classList.add("hidden");
        }
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // Only set processing if component is mounted
    setProcessing(value) {
        isMounted && this.setState({ processing: value });
    }

    // Check if the image captured is above the max size
    isLargeImage(imageWidth, imageHeight, imageSize) {
        return (
            imageWidth > environment.maxWidth ||
            imageHeight > environment.maxHeight ||
            imageSize > environment.maxFileSize
        );
    }

    onCaptured(response) {
        DataDogService.log("Image Captured Successfully");
        this.clearLoading();
        isMounted && this.setState({ receivedImage: true });
        this.setProcessing(true);
    }

    onCropped(response) {
        this.setProcessing(false);

        if (response) {
            let imageType = this.getImageType();

            let properties = {
                imageSize: response.image.size,
                width: response.image.width,
                height: response.image.height,
                originalWidth: response.image.originalWidth,
                originalHeight: response.image.originalHeight,
                properties: {
                    sharpness: response.sharpness,
                    glare: response.glare,
                    dpi: response.dpi,
                    documentId: this.props.location.state.documentId,
                    isPassport: response.isPassport,
                },
            };

            // Check if downscaled image is required.
            const isDownscaledImageRequired = this.isLargeImage(
                response.image.originalWidth,
                response.image.originalHeight,
                response.image.size
            );

            const { setCapturedDocumentImage } = this.props;

            // Set ONLY downscaled or uncropped image
            const setUncroppedOrDownscaledImage = () => {
                if (isDownscaledImageRequired) {
                    setCapturedDocumentImage(
                        response.image.downscaledData,
                        "downscaled"
                    );
                } else {
                    setCapturedDocumentImage(
                        response.image.uncroppedData,
                        "uncropped"
                    );
                }
            };

            // Determines which image(s) to set based on 'imageType' and 'response.cropped'.
            if (imageType === "cropped" && response.cropped) {
                // Image type is cropped and cropped image is available
                // Set ONLY the 'cropped' image.
                setCapturedDocumentImage(response.image.data, "cropped");
            } else if (imageType === "uncropped" || !response.cropped) {
                // Image type is uncropped or no cropped image is available
                // Set uncropped or downscaled
                setUncroppedOrDownscaledImage();
            } else {
                // Default: set both cropped & (uncropped or downscaled)
                setCapturedDocumentImage(response.image.data, "cropped");
                setUncroppedOrDownscaledImage();
            }

            // Only log error if uncropped and not a paper licence
            if (
                !response.cropped &&
                this.props.location.state.documentId !== 0
            ) {
                DataDogService.log("Could not crop image.");
            }

            if (response.cropped) {
                DataDogService.log("Image Cropped Successfully");
            }

            // Show error modal on cropping errors until the user is on their last attempt
            // To prevent the preview screen from showing twice
            if (
                !response.cropped &&
                this.props.submissionAttempts.remaining > 1 &&
                this.props.location.state.documentId !== 0
            ) {
                isMounted &&
                    this.setState({
                        previewImage: response.image.uncroppedData,
                    });
                isMounted && this.setState({ cropMessage: true });
            }

            // Only proceed if we have a cropped image,
            // On last submission attempt, or is a paper licence
            if (
                response.cropped ||
                this.props.submissionAttempts.remaining <= 1 ||
                this.props.location.state.documentId === 0
            ) {
                isMounted &&
                    this.setState({
                        navigation: {
                            action: "next",
                            props: properties,
                        },
                    });
            }
        }
    }

    onFrameAvailable(response) {}

    startCamera() {
        isMounted && this.setState({ autoCaptureError: false });

        if (AcuantCameraUI) {
            document.body.onfocus = this.smoothLoading;

            // Auto capture not enabled, or paper licence manual capture.
            if (
                !this.props.autoIdCaptureSupported ||
                this.props.location.state.documentId === 0
            ) {
                this.manualCapture();
            } else if (
                this.props.autoIdCaptureSupported &&
                AcuantCamera.isCameraSupported
            ) {
                // Perform auto capture if the device supports it
                this.autoCapture();
            } else {
                // fallback to manual capture if there are any uncaught errors
                !this.state.autoCaptureError && this.manualCapture();
            }
        }
    }

    autoCapture() {
        const { t } = this.props;
        isMounted && this.setState({ autoCaptureStarted: true });
        const options = {
            text: {
                NONE: t("idpal_align"),
                SMALL_DOCUMENT: t("idpal_closer"),
                GOOD_DOCUMENT: null, //null countdown
                BIG_DOCUMENT: t("idpal_id_too_close"),
                CAPTURING: t("idpal_capturing"),
                TAP_TO_CAPTURE: t("idpal_tap_to_capture"),
            },
        };

        try {
            AcuantCameraUI.start(
                {
                    onCaptured: this.onCaptured.bind(this),
                    onCropped: this.onCropped.bind(this),
                    onFrameAvailable: this.onFrameAvailable.bind(this),
                },
                this.onError.bind(this),
                options
            );
        } catch (error) {
            DataDogService.error(error);
        }
    }

    manualCapture() {
        let imageType = this.getImageType();

        try {
            AcuantCamera.startManualCapture(
                {
                    onCaptured: this.onCaptured.bind(this),
                    onCropped: this.onCropped.bind(this),
                },
                imageType,
                this.onError.bind(this)
            );
        } catch (error) {
            DataDogService.error(error);
        }
    }
    getListOfAcceptedDocumentTitles() {
        const { t } = this.props;

        const acceptedDocuments = this.props.acceptedDocuments.map(
            title => t(titleMap[title]) || title
        );

        return makeCommaSeparatedString(acceptedDocuments, t("idpal_or"));
    }

    getImageType() {
        const passportCropSettings = this.props.cropSettings.passport;
        const idCardCropSettings = this.props.cropSettings.id_card;

        if (
            passportCropSettings !== idCardCropSettings ||
            (passportCropSettings === 0 && idCardCropSettings === 0)
        ) {
            return "both";
        } else if (this.props.cropSettings.passport === 1) {
            return "cropped";
        } else {
            return "uncropped";
        }
    }

    onError(error) {
        // If there is an error loading acuant auto capture
        // sets the error message to start manual fallback
        AcuantCamera.isCameraSupported = false;
        this.clearLoading();
        this.props.setAutoIDCaptureError();
        isMounted &&
            this.setState({
                autoCaptureError: true,
            });
        logService.error(error);
    }

    handleCancel() {
        if (!this.state.receivedImage) {
            this.goBack();
        }
    }

    goBack() {
        isMounted &&
            this.setState({
                navigation: {
                    action: "back",
                    props: {
                        documentId: this.props.cardType,
                        sidesLeft: this.props.sidesLeft,
                    },
                },
            });
    }

    retryPhoto() {
        this.props.clearCapturedDocumentImages();
        this.clearLoading();
        this.clearCameraStreams();
        isMounted &&
            this.setState({
                receivedImage: false,
                cropMessage: false,
                previewImage: null,
                processing: false,
                autoCaptureError: false,
            });
        // Decrement submission attempts before classification
        this.props.submissionAttempt("front");
        this.startCamera();
    }

    render() {
        const { t, ImageContainer } = this.props;
        const formattedDocs = this.getListOfAcceptedDocumentTitles();

        if (this.state.processing) {
            return (
                <ProcessingLayout
                    documentData={this.props.location.state}
                    showMessages={true}
                />
            );
        }
        // Document upload is already completed
        if (this.props.completed) {
            return (
                <div className="root-layout-container acuant-camera-wrapper">
                    <Header />
                    <StageComplete
                        continue={() =>
                            this.setState({ navigation: { action: "next" } })
                        }
                        message="idpal_document_captured"
                    />
                    <Navigator
                        page={"document_review"}
                        action={this.state.navigation.action}
                        propsToPass={this.state.navigation.props}
                    />
                </div>
            );
        }

        // Out of retries, and we've already tried this side
        if (
            this.props.submissionAttempts.remaining <= 0 &&
            this.props.sidesLeft === 0 &&
            this.props.submissionAttempts[
                this.props.sidesLeft === 2 ? "front" : "back"
            ] > 0
        ) {
            return (
                <div className="root-layout-container acuant-camera-wrapper">
                    <Header />
                    <OutOfRecaptures
                        continue={() => {
                            history.goForward();
                        }}
                        message="idpal_out_of_attempts"
                    />
                    <Navigator
                        page={"document_capture"}
                        action={this.state.navigation.action}
                        propsToPass={this.state.navigation.props}
                    />
                </div>
            );
        }

        return (
            <div className="root-layout-container acuant-camera-wrapper processed-image-result-page">
                {/* Modal popup instructions  */}
                {this.state.cropMessage &&
                    this.props.submissionAttempts.remaining >= 0 &&
                    (this.props.isDocSelectionEnabled ? (
                        <ModalPopupOverlay
                            Icon={FailedToAcceptDocumentIcon}
                            popupTitle={t(
                                "idpal_popup_title_doc_not_recognized"
                            )}
                            popupText={
                                <Trans
                                    i18nKey="idpal_popup_text_retake_a_picture"
                                    values={{ documentTypes: formattedDocs }}
                                />
                            }
                        />
                    ) : (
                        <ModalPopupOverlay
                            Icon={FailedToAcceptDocumentIcon}
                            popupTitle={t("idpal_no_image_detected")}
                            popupText={t("idpal_no_image_user_message")}
                        />
                    ))}
                <Header />
                <div id="acuant-camera"></div>
                <div
                    id={"loader"}
                    className="u-text-center start-loading hidden"
                >
                    <LoadingStateAnimation />
                </div>

                {!this.state.processing && (
                    <div id={"loading"} className="cta-container hidden">
                        <CustomButton
                            id={"back"}
                            className={"btn hidden"}
                            handleClick={() => this.handleCancel()}
                            actionDataLabel={ACTION.backHiddenButton}
                        />
                    </div>
                )}

                {/* Show preview of badly cropped image */}
                {this.state.cropMessage &&
                    this.props.submissionAttempts.remaining >= 0 && (
                        <>
                            <SubmissionFlowStepper />
                            <div className="error-message-text">
                                {t("idpal_classification_error")}
                            </div>
                            <ImageContainer
                                cardImage={this.state.previewImage}
                                imageAltText={imageAlt.upLoadedDocument}
                                src={this.state.previewImage}
                            />
                            <div
                                className="cta-container"
                                style={{
                                    display: "flex",
                                    margin: "10px 0px",
                                    gap: "10px",
                                }}
                            >
                                <CustomButton
                                    id={"retry"}
                                    className={"btn"}
                                    label={t("idpal_retry")}
                                    handleClick={() => this.retryPhoto()}
                                    actionDataLabel={ACTION.retryButton}
                                />
                            </div>
                        </>
                    )}

                {!this.state.processing &&
                    this.props.submissionAttempts.remaining >= 0 &&
                    !this.state.cropMessage && (
                        <>
                            {this.state.autoCaptureError ? (
                                <div className="o-site-wrap instructions">
                                    <div
                                        id="error"
                                        className="u-generic-text u-text-center u-md-btm-buffer error-buffer"
                                    >
                                        {t(
                                            "idpal_auto_id_capture_error_message"
                                        )}
                                    </div>
                                    <div className="cta-container">
                                        <CustomButton
                                            id={"capture"}
                                            className={"btn"}
                                            label={t("idpal_manual_capture")}
                                            handleClick={() =>
                                                this.startCamera()
                                            }
                                            actionDataLabel={
                                                ACTION.captureButton
                                            }
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={
                                        this.state.autoCaptureStarted
                                            ? "hidden"
                                            : "capture-upload-page u-flex u-flex--row u-flex--vh u-flex--center p-success"
                                    }
                                >
                                    <div className="cta-container">
                                        <CustomButton
                                            id={"capture"}
                                            className={"btn"}
                                            label={t("idpal_retry")}
                                            handleClick={() =>
                                                this.retryPhoto()
                                            }
                                            actionDataLabel={ACTION.retryButton}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                <video
                    id="acuant-player"
                    controls
                    autoPlay
                    playsInline
                    style={{ display: "none" }}
                >
                    <track kind="captions" />
                </video>
                <div style={{ textAlign: "center" }}>
                    <canvas
                        id="acuant-video-canvas"
                        width="100%"
                        height="auto"
                    ></canvas>
                </div>

                <Navigator
                    page={"document_capture"}
                    action={this.state.navigation.action}
                    propsToPass={this.state.navigation.props}
                />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        cardType: state.idProperties.cardType,
        submissionAttempts: state.submissionState.submissionAttempts,
        completed:
            state.submissionState.submissionState.document_upload.completed,
        cropSettings: state.config.cropSettings,
        sidesLeft: state.idProperties.sidesLeft,
        autoIdCaptureSupported: state.config.autoIdCaptureSupported,
        autoIdCaptureError: state.config.autoIdCaptureError,
        branding: state.config.profile,
        acceptedDocuments: state.config.profile.data.accepted_documents,
        isDocSelectionEnabled: state.submissionState.isDocSelectionEnabled,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            setRetryLimit,
            submissionAttempt,
            setAutoIDCaptureError,
            setCapturedDocumentImage,
            clearCapturedDocumentImages,
        },
        dispatch
    );
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(AcuantComponentWrapper)
);
